import Animated, {
  useAnimatedStyle,
  useDerivedValue,
  useSharedValue,
  withTiming,
} from "react-native-reanimated";
import { AHText, Box, useLayoutContext, useTheme, AHIcon } from "arkh";
import { Image, Platform, View } from "react-native";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import React, { useCallback, useMemo } from "react";

import { CircleButton } from "./CircleButton";

import { WidgetNoteText } from "./WidgetNoteText";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { graphql, useFragment } from "react-relay";
import { WidgetNotePageImageMode_widgetNotePage$key } from "./__generated__/WidgetNotePageImageMode_widgetNotePage.graphql";

import { Slider } from "@miblanchard/react-native-slider";
import { useNoteWidgetPageTextStyle } from "widgets/src/WidgetNote/styleHelpers/useNoteWidgetStyle";
import { useToggleFontColor } from "./useToggleFontColor";

type Props = {
  noteId: string;
  image: {
    uri: string;
    width: number;
    height: number;
    imageId?: string;
  };
  page: WidgetNotePageImageMode_widgetNotePage$key;
  widgetLayout: {
    width: number;
    height: number;
  };
  unitSize: number;
  onCancel(): void;

  onSave(image: {
    id: string;
    uri: string;
    translateX: number;
    translateY: number;
    scale: number;
    width: number;
    height: number;
  }): void;
};

export const WidgetNoteImageMode = React.memo(WidgetNoteImageModeC);
function WidgetNoteImageModeC(props: Props) {
  const page = useFragment(
    graphql`
      fragment WidgetNotePageImageMode_widgetNotePage on WidgetNotePage {
        ...WidgetNoteText_page
        id
        fontColor
        ...useToggleFontColor_page
        ...useNoteWidgetStyleText_widgetNotePage
      }
    `,
    props.page
  );

  const textStyle = useNoteWidgetPageTextStyle(page, { forceImage: true });

  const toggleColor = useToggleFontColor({
    page,
    noteId: props.noteId,
  });
  const theme = useTheme();

  const layout = useLayoutContext();
  const insets = useSafeAreaInsets();

  const scale = useSharedValue(1);
  const translateX = useSharedValue(0);
  const translateY = useSharedValue(0);
  const animatedStyle = useAnimatedStyle(() => ({
    transform: [
      {
        translateX: translateX.value,
      },
      {
        translateY: translateY.value,
      },
      {
        scale: scale.value,
      },
    ],
  }));

  const startX = useSharedValue(0);
  const startY = useSharedValue(0);

  const pan = Gesture.Pan()
    .onStart((event) => {
      startX.value = translateX.value;
      startY.value = translateY.value;
    })
    .onChange((event) => {
      translateY.value = (startY.value ?? 0) + event.translationY;
      translateX.value = (startX.value ?? 0) + event.translationX;
    });

  const startScale = useSharedValue(1);
  const pinch = Gesture.Pinch()
    .onStart((event) => {
      startScale.value = scale.value;
    })
    .onChange((event) => {
      scale.value = startScale.value * event.scale;
    })
    .onEnd((event) => {
      scale.value = startScale.value * event.scale;
    });

  const gesture = Gesture.Simultaneous(pan, pinch);

  const scaleFactor = useMemo(
    () =>
      props.widgetLayout.width / props.widgetLayout.height >
      props.image.width / props.image.height
        ? props.widgetLayout.width / props.image.width
        : props.widgetLayout.height / props.image.height,
    [
      props.image.height,
      props.image.width,
      props.widgetLayout.height,
      props.widgetLayout.width,
    ]
  );

  const imageHeight = useMemo(
    () => props.image.height * scaleFactor,
    [props.image.height, scaleFactor]
  );
  const imageWidth = useMemo(
    () => props.image.width * scaleFactor,
    [props.image.width, scaleFactor]
  );

  const [sliderKey, setSliderKey] = React.useState(0);

  const toggleContainFull = () => {
    translateX.value = withTiming(0);
    translateY.value = withTiming(0);
    if (scale.value === 1) {
      scale.value = withTiming(
        props.widgetLayout.width / props.widgetLayout.height <
          imageWidth / imageHeight
          ? props.widgetLayout.width / imageWidth
          : props.widgetLayout.height / imageHeight
      );
    } else {
      scale.value = withTiming(1);
    }
    setSliderKey((prev) => prev + 1);
  };

  const buttonMode = useDerivedValue(() =>
    scale.value === 1 ? "fill" : "cover"
  );

  const coverButtonWrapperStyle = useAnimatedStyle(() => ({
    opacity: withTiming(buttonMode.value === "fill" ? 0 : 1),
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  }));
  const fillButtonWrapperStyle = useAnimatedStyle(() => ({
    opacity: withTiming(buttonMode.value === "cover" ? 0 : 1),
    position: "absolute",
    justifyContent: "center",
    alignItems: "center",
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
  }));

  const saveImage = useCallback(() => {
    if (props.image.imageId) {
      props.onSave({
        id: props.image.imageId,
        uri: props.image.uri,
        translateX: translateX.value / props.unitSize,
        translateY: translateY.value / props.unitSize,
        scale: scale.value,
        width: props.image.width,
        height: props.image.height,
      });
    }
  }, [props, scale.value, translateX.value, translateY.value]);

  return (
    <Box
      color="background"
      style={{
        position: "absolute",
        zIndex: 1,
        left: 0,
        top: 0,
        right: 0,
        bottom: 0,
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    >
      <View>
        <GestureDetector gesture={gesture}>
          <Animated.View
            collapsable={false}
            style={[
              {
                position: "absolute",
                top: (props.widgetLayout.height - imageHeight) / 2,
                left: (props.widgetLayout.width - imageWidth) / 2,
                width: imageWidth,
                height: imageHeight,
              },
              animatedStyle,
            ]}
          >
            <Image
              source={{
                uri: props.image.uri,
                cache: "force-cache",
              }}
              style={{
                width: "100%",
                height: "100%",
              }}
            />
          </Animated.View>
        </GestureDetector>

        <View
          pointerEvents="none"
          style={{
            ...{ userSelect: "none" },
            borderWidth: 4,
            justifyContent: "flex-start",
            width: props.widgetLayout.width + 4,
            height: props.widgetLayout.height + 4,
            left: -2,
            top: -2,
            borderColor: theme.primary,
            borderRadius: layout.space.medium,
          }}
        >
          <WidgetNoteText page={page} />
        </View>
      </View>
      <Box
        style={{
          position: "absolute",
          bottom: 0,
          width: "100%",
          height: 50 + layout.space.small * 2 + insets.bottom,
          paddingBottom: insets.bottom,
        }}
        color="surface"
      >
        <CircleButton
          onPress={props.onCancel}
          style={{
            left: layout.space.medium,
            top: layout.space.small,
          }}
        >
          <AHIcon type="clear" forceSize={30} customColor={theme["text-primary"]} />
        </CircleButton>
        <CircleButton
          onPress={toggleContainFull}
          style={{
            left: layout.space.medium + 70,
            top: layout.space.small,
          }}
        >
          <Animated.View style={coverButtonWrapperStyle}>
            <AHIcon type="expand-outline" forceSize={30} customColor={theme["text-primary"]} />
          </Animated.View>
          <Animated.View style={fillButtonWrapperStyle}>
            <AHIcon type="scan" forceSize={30} customColor={theme["text-primary"]} />
          </Animated.View>
        </CircleButton>
        <CircleButton
          onPress={toggleColor}
          style={{
            left: layout.space.medium + 140,
            top: layout.space.small,
          }}
        >
          <AHText
            style={[
              {
                fontStyle: textStyle.fontStyle,
                fontFamily: textStyle.fontFamily,
                textAlign: textStyle.textAlign,
                color: textStyle.color,
              },
              textStyle.textShadow,
            ]}
          >
            Aa
          </AHText>
        </CircleButton>
        {Platform.OS === "web" ? (
          <Slider
            key={sliderKey}
            containerStyle={{ flex: 1, width: 300, alignSelf: "center" }}
            minimumValue={0.5}
            maximumValue={3}
            value={scale.value}
            thumbTintColor={theme.primary}
            maximumTrackTintColor={theme.washedText}
            minimumTrackTintColor={theme.primary}
            onValueChange={(value) => {
              scale.value = value[0];
            }}
          />
        ) : null}
        <CircleButton
          color="primary"
          onPress={saveImage}
          style={{
            right: layout.space.medium,
            top: layout.space.small,
          }}
          loading={!props.image.imageId}
        >
          <AHIcon type="check" forceSize={30} customColor={theme.surface} />
        </CircleButton>
      </Box>
    </Box>
  );
}
